import { useState } from "react";
import { useAuth } from "../AuthContext";
import StoreSelectModal from "./StoreSelectModal";
import PinOverlay from "./PinOverlay";
import MasterView from "./pages/MasterView";
import Users from "./pages/Users";
import { StoreDashboard, Performance, Rules, Approvals, Clients, AuditLog, Settings } from "./pages/SimplePages";

const ROLE_LABEL = { master: "👑 Master", client_admin: "🏢 Client Admin", planner: "📋 Planner", view_only: "👁️ View Only" };

// section availability per role
const SECTIONS = [
  { id: "master", label: "Master View", icon: "👑", group: "Overview", roles: ["master"] },
  { id: "dashboard", label: "Store Dashboard", icon: "⚡", group: "Overview", roles: ["master", "client_admin", "planner", "view_only"] },
  { id: "performance", label: "Performance", icon: "📊", group: "Overview", roles: ["master", "client_admin", "planner", "view_only"] },
  { id: "rules", label: "Rules Engine", icon: "⚙️", group: "Automation", roles: ["master", "client_admin", "planner"] },
  { id: "approvals", label: "Approvals", icon: "✅", group: "Automation", roles: ["master", "client_admin"], badge: 2 },
  { id: "clients", label: "Clients & Stores", icon: "🏪", group: "Admin", roles: ["master", "client_admin"] },
  { id: "users", label: "Users", icon: "👥", group: "Admin", roles: ["master"] },
  { id: "logs", label: "Audit Log", icon: "📋", group: "Admin", roles: ["master"] },
  { id: "settings", label: "API Settings", icon: "🔑", group: "Admin", roles: ["master"] },
];

export default function AppShell() {
  const { user, logout, activeStore, setActiveStore } = useAuth();
  const [section, setSection] = useState(user.role === "master" ? "master" : "dashboard");
  const [showStoreSelect, setShowStoreSelect] = useState(false);
  const [pinStore, setPinStore] = useState(null);

  const available = SECTIONS.filter((s) => s.roles.includes(user.role));
  const groups = [...new Set(available.map((s) => s.group))];

  const initials = (user.name || user.email).slice(0, 4);

  const onStorePick = (store) => {
    setShowStoreSelect(false);
    setPinStore(store);
  };
  const onPinDone = (store) => {
    setPinStore(null);
    setActiveStore(store);
    setSection("dashboard");
  };

  const renderSection = () => {
    switch (section) {
      case "master": return <MasterView />;
      case "dashboard": return <StoreDashboard activeStore={activeStore} />;
      case "performance": return <Performance />;
      case "rules": return <Rules />;
      case "approvals": return <Approvals />;
      case "clients": return <Clients />;
      case "users": return <Users />;
      case "logs": return <AuditLog />;
      case "settings": return <Settings />;
      default: return null;
    }
  };

  return (
    <div className="app" data-testid="app-shell">
      <aside className="sidebar">
        <div className="sidebar-top">
          <div className="brand">Admatik <span>Rmax</span></div>
          <div className="brand-sub">Command Centre · v2.0</div>
        </div>

        <div className="sidebar-scroll">
        <div className="context-pill" onClick={() => setShowStoreSelect(true)} data-testid="context-pill">
          <div className="context-platform">
            <div className="platform-dot" style={{ background: activeStore ? `var(--${activeStore.platform})` : "var(--muted)" }} />
            <span>{activeStore ? `${activeStore.platform} ${activeStore.country}` : "No store selected"}</span>
          </div>
          <div className="context-store">{activeStore ? `${activeStore.name} · ${activeStore.shop_id}` : "Click to select"}</div>
          <div className="context-change">↕ Switch store</div>
        </div>

        {groups.map((g) => (
          <div key={g}>
            <div className="nav-section">{g}</div>
            {available.filter((s) => s.group === g).map((s) => (
              <button key={s.id}
                className={`nav-item ${section === s.id ? "active" : ""}`}
                onClick={() => setSection(s.id)}
                data-testid={`nav-${s.id}`}>
                <span className="nav-icon">{s.icon}</span> {s.label}
                {s.badge && <span className="nav-badge">{s.badge}</span>}
              </button>
            ))}
          </div>
        ))}
        </div>

        <div className="sidebar-footer">
          <div className="user-card">
            <div className="user-av">{initials}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="user-name" style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{user.name}</div>
              <div className="user-tier">{ROLE_LABEL[user.role]}</div>
            </div>
            <button className="logout-btn" onClick={logout} data-testid="logout-btn" title="Logout">⏻</button>
          </div>
        </div>
      </aside>

      <main className="main">{renderSection()}</main>

      {showStoreSelect && <StoreSelectModal onClose={() => setShowStoreSelect(false)} onPick={onStorePick} />}
      {pinStore && <PinOverlay store={pinStore} onDone={onPinDone} onCancel={() => setPinStore(null)} />}
    </div>
  );
}
